import { ReactNode, useState } from "react";
import { useSubscription } from "@/hooks/use-subscription";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";

// Define the subscription plan hierarchy
export const SUBSCRIPTION_LEVELS = {
  FREE: 0,
  NOBILITY: 10,
  ROYALTY: 20,
  IMPERIAL: 30,
};

// Map subscription plan slugs to their level
export const SUBSCRIPTION_LEVEL_MAP: Record<string, number> = {
  free: SUBSCRIPTION_LEVELS.FREE,
  nobility: SUBSCRIPTION_LEVELS.NOBILITY,
  royalty: SUBSCRIPTION_LEVELS.ROYALTY,
  imperial: SUBSCRIPTION_LEVELS.IMPERIAL,

  // Legacy plan mappings
  educator: SUBSCRIPTION_LEVELS.NOBILITY,
  premium: SUBSCRIPTION_LEVELS.ROYALTY,
  basicSeller: SUBSCRIPTION_LEVELS.NOBILITY,
  premiumSeller: SUBSCRIPTION_LEVELS.NOBILITY,
  basicDirectory: SUBSCRIPTION_LEVELS.FREE,
  premiumDirectory: SUBSCRIPTION_LEVELS.ROYALTY,
  annualAccess: SUBSCRIPTION_LEVELS.ROYALTY,
  quarterlyAccess: SUBSCRIPTION_LEVELS.ROYALTY,
  monthlyAccess: SUBSCRIPTION_LEVELS.ROYALTY,
};

interface RequireSubscriptionProps {
  children: ReactNode;
  level: number;
  feature?: string;
  description?: string;
  fallback?: ReactNode;
  message?: string;
}

export default function RequireSubscription({
  children,
  level,
  feature,
  description,
  fallback,
  message = "This feature requires a higher subscription level.",
}: RequireSubscriptionProps) {
  const { userSubscriptionLevel } = useSubscription();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [, navigate] = useLocation();

  // Check if the user has sufficient access
  const hasAccess = userSubscriptionLevel >= level;

  // If the user has access, render the children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If there's a fallback, render it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Otherwise, render a button that opens the upgrade modal
  return (
    <>
      <div className="p-4 border rounded-lg bg-gray-800 text-center">
        <p className="mb-4">{message}</p>
        <Button
          onClick={() => setUpgradeModalOpen(true)}
          className="bg-brand text-black hover:bg-brand/90"
        >
          Upgrade Subscription
        </Button>
      </div>

      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="bg-gray-800 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle>Subscription Upgrade Required</DialogTitle>
            <DialogDescription className="text-gray-300">
              {feature ? `${feature} requires` : 'This feature requires'} a {
                level >= SUBSCRIPTION_LEVELS.IMPERIAL ? 'Imperial' :
                level >= SUBSCRIPTION_LEVELS.ROYALTY ? 'Royalty' :
                level >= SUBSCRIPTION_LEVELS.NOBILITY ? 'Nobility' : 'Free'
              } subscription or higher. {description && `${description}.`} Would you like to upgrade your subscription now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUpgradeModalOpen(false)}
            >
              Not Now
            </Button>
            <Button
              onClick={() => navigate("/subscription/upgrade")}
              className="bg-brand text-black hover:bg-brand/90"
            >
              View Subscription Options
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Export both as default and named export for flexibility
export { RequireSubscription };
